import { useDispatch, useSelector } from 'react-redux'
import {
  useVerificationFlow,
  VerificationFlowProvider
} from '../../../../../../../shared/VerificationFlow/VerificationFlowContext'
import { useVerificationFlowEnabled } from '../../../../../../../../hooks'
import {
  selectPreferredLoanOfficerMembership,
  selectPreferredRealEstateAgentMembership
} from '../../../../../../../../store/selectors/clientTeamMemberships'
import { useEffect } from 'react'
import VerifyingView from '../../../../../../../shared/VerificationFlow/components/views/VerifyingView'
import { Flex } from '@homebotapp/hb-react-component-catalog'
import { StartPrequalView } from '../../../../../../../shared/VerificationFlow/components/views/StartPrequalView'
import { SendMessageView } from './SendMessageView'
import { Listing } from '../../../../../../../../api/gqlaxy/gql/generated/graphql'
import { UnverifiedView } from './UnverifiedView'
import { RemovePartnerView } from './RemovePartnerView'
import { AddPartnerView } from './AddPartnerView'
import { VerificationPrequalView } from './VerificationPrequalView'
import { SendPrequalView } from './SendPrequalView'
import Step from '../../../../../ModalStepsOutline'
import {
  QUESTIONS,
  selectScheduleTourPreferredAgent,
  selectScheduleTourStep,
  setScheduleTourShowModal,
  setScheduleTourStep,
  setScheduleTourStepAdvanceOneStep,
  STEPS,
  STEPS_CONST,
  STEPS_REA
} from '../../../../../../../../store/slices/scheduleTour'
import { selectCustomerIsRealEstateAgent } from '../../../../../../../../store/selectors/customerProfile'
import styles from './VerificationFlow.module.scss'
import { CUSTOMER_MESSAGES } from '../../../../../../../../constants/customerMessages'

const Content = ({ listing }: { listing: Listing }) => {
  const dispatch = useDispatch()
  const tourStep = useSelector(selectScheduleTourStep)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const stepIndex = STEPS.indexOf(tourStep)

  const {
    startVerification,
    state: { currentStep, onComplete },
    dispatch: verificationDispatch
  } = useVerificationFlow()

  useEffect(() => {
    // figure out what these are going to be
    startVerification(
      '',
      // Looks like we always use this topic key, but
      // sendListingMessage takes a sendToPreferredAgent
      // prop that I guess changes who the email goes to?
      CUSTOMER_MESSAGES.LISTINGS_SHOWING_REQUEST.topicKey,
      CUSTOMER_MESSAGES.LISTINGS_SHOWING_REQUEST.topicKey
    )
  }, [])

  // Handle completion of the verification flow
  useEffect(() => {
    if (currentStep === 'complete' && onComplete) {
      verificationDispatch({ type: 'RESET' })
      onComplete()
    }
  }, [currentStep, onComplete])

  if (currentStep === 'verifying') {
    return <VerifyingView />
  }

  return (
    <Step stepLabel='schedule-tour-verification' className={styles.step}>
      <Step.HeaderNavSection
        currentStep={QUESTIONS}
        steps={isCustomerREA ? STEPS_REA : STEPS}
        onCancel={() => dispatch(setScheduleTourShowModal(false))}
        onBack={() => dispatch(setScheduleTourStep(STEPS_CONST[stepIndex - 1]))}
      >
        Verification
      </Step.HeaderNavSection>
      <Step.Body>
        <Flex direction='column' flex={1} justifyContent='space-between' textAlign='left'>
          {currentStep === 'messaging' && <SendMessageView listing={listing} />}
          {currentStep === 'unverified' && <UnverifiedView />}
          {currentStep === 'removePartner' && <RemovePartnerView />}
          {currentStep === 'addPartner' && <AddPartnerView />}
          {currentStep === 'startPrequal' && <StartPrequalView />}
          {currentStep === 'verificationPrequal' && <VerificationPrequalView listing={listing} />}
          {currentStep === 'sendPrequal' && <SendPrequalView listing={listing} />}
        </Flex>
      </Step.Body>
    </Step>
  )
}

export const VerificationFlow = ({
  listing,
  onComplete,
  onSkip
}: {
  listing: Listing
  onComplete: () => void
  onSkip: () => void
}) => {
  const dispatch = useDispatch()
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const realEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const agentPreference = useSelector(selectScheduleTourPreferredAgent)

  // If verification flow is disabled or
  // if the agent preference is set to the listing agent,
  // skip the verification flow and continue with the normal ScheduleTour flow
  if (!isVerificationFlowEnabled || agentPreference === 'listingAgent') {
    dispatch(setScheduleTourStepAdvanceOneStep())
  }

  return (
    <VerificationFlowProvider
      options={{
        enableVerificationFlow: isVerificationFlowEnabled,
        initialPartnerType: 'RealEstateAgent',
        realEstateAgentMembership: realEstateAgentMembership,
        loanOfficerMembership: loanOfficerMembership,
        includePrequal: true,
        onComplete: onComplete,
        onSkip
      }}
    >
      <Content listing={listing} />
    </VerificationFlowProvider>
  )
}
